import React, { useState, useContext } from 'react';
import './StepTwoComponent.scss';
import { getBulkAccessCode } from '../../network/ApiService';
import { AppContext } from '../../App';
import ResourceListMessage from './ResourceListMessage';

const StepTwoComponent = ({
  setStep,
  onGenerateCodes,
  isOpen,
  setCurrentRequestId,
  setLoading,
  onError,
}) => {
  const { isbn, ppdNumber } = useContext(AppContext);
  const [codeType, setCodeType] = useState('');
  const [numberOfCodes, setNumberOfCodes] = useState('');
  const [reason, setReason] = useState('');
  const [printISBN, setPrintISBN] = useState('');
  const [errors, setErrors] = useState({
    codeType: false,
    numberOfCodes: false,
  });
  const [showCommonError, setShowCommonError] = useState(false);

  const fetchPmrId = () => {
    const ProductModelregionData = JSON.parse(
      localStorage.getItem('orgDetailsByUser')
    );
    return ProductModelregionData?.organisationDetails?.data[0]
      ?.productModelRegions[0]?.id;
  };

  const isNumberValid = () => {
    if (!numberOfCodes) return false;
    const num = Number(numberOfCodes);
    return Number.isInteger(num) && num >= 1 && num <= 5000;
  };

  if (!isOpen) return null;

  const handleGenerate = async () => {
    const newErrors = {
      codeType: !codeType,
      numberOfCodes: !isNumberValid(),
    };

    setErrors(newErrors);

    const bothInvalid = newErrors.codeType && newErrors.numberOfCodes;
    setShowCommonError(bothInvalid);

    if (newErrors.codeType || newErrors.numberOfCodes) {
      return; // stop if any error
    }

    setShowCommonError(false);

    const payload = {
      numberOfCode: parseInt(numberOfCodes, 10),
      validFrom: new Date().toISOString(),
      isbn: isbn,
      lob: 'AH',
      accessCodeType: codeType,
      productId: ppdNumber,
      numberOfUse: 1,
      notes: reason,
      associateIsbn: printISBN || null,
    };

    onGenerateCodes(payload);

    const pmrid = fetchPmrId();
    setLoading(true);

    const subscription = getBulkAccessCode(pmrid, payload).subscribe(
      (response) => {
        console.log(response);
        setCurrentRequestId(response.requestId);
        setStep(3);
       let delay = (parseInt(numberOfCodes, 10) *2 ) + 5000;
        setTimeout(() => {
            setLoading(false);
        }, delay);
      },
      (error) => {
        setStep(2);
        onError(error);
        setLoading(false);
        console.log(error);
      }
    );

    return () => subscription.unsubscribe();
  };


  const handleCodeTypeChange = (e) => {
    const value = e.target.value;
    setCodeType(value);

    const numberValid = isNumberValid();

    // Update individual field error
    setErrors(prev => ({ ...prev, codeType: !value }));

    // Hide common error if either field valid
    if (value || numberValid) {
      setShowCommonError(false);
    }
  };

  const handleNumberChange = (e) => {
    const val = e.target.value;

    if (val === '') {
      setNumberOfCodes('');
      setErrors(prev => ({ ...prev, numberOfCodes: true }));
      if (codeType) setShowCommonError(false); // if codeType valid, hide common error
      return;
    }

    if (!/^\d+$/.test(val)) return;

    const num = Number(val);

    if (num < 1 || num > 5000) return;

    setNumberOfCodes(val);
    setErrors(prev => ({ ...prev, numberOfCodes: false }));

    // Hide common error if either field valid
    if (codeType || isNumberValid()) {
      setShowCommonError(false);
    }
  };


  return (
    <div className="step-two-container">
      <button className="back-btn" onClick={() => setStep(1)}>
        <img
          className="back-icon"
          src={`${process.env.PUBLIC_URL}/images/right-arrowdark.svg`}
          alt="arrow right"
        />
        <span>Back to Enter ISBN13 or PPID</span>
      </button>

      {showCommonError && (
        <ResourceListMessage onClose={() => setShowCommonError(false)} />
      )}

      <div className="fields-scroll-container">
        <h5 className="add-details-heading">Add details and generate codes</h5>

        {/* Code Type */}
        <div className="field-group code-type-dropdown">
          <label htmlFor="codeType" className="field-label">
            Code type
          </label>
          <p className="help-text">Select the code type</p>

          {errors.codeType && (
            <div className="isbn-invalid">
              <img
                className="isbn-invalid-icon"
                src={`${process.env.PUBLIC_URL}/images/error-new.svg`}
                alt="Invalid"
              />
              <span>Please select a code type.</span>
            </div>
          )}
          <div className="dropdown-wrapper">
            <select
              id="codeType"
              className={`dropdown-select ${errors.codeType ? 'input-error' : ''}`}
              value={codeType}
              onChange={handleCodeTypeChange}
            >
              <option value="" disabled>
                Code type
              </option>
              <option value="BB">Book code</option>
              <option value="DC">D2C code</option>
            </select>

            <img
              src={`${process.env.PUBLIC_URL}/images/dropdown-arrowstep2.svg`}
              alt="Dropdown Arrow"
              className="dropdown-arrow-icon"
            />
          </div>
        </div>

        {/* Number of Codes */}
        <div className="field-group number-input-group">
          <label htmlFor="numberOfCodes" className="field-label">
            Enter the number of student ebook codes required
          </label>
          <p className="help-text">Maximum 5000 codes per request</p>
          {errors.numberOfCodes && (
            <div className="isbn-invalid">
              <img
                className="isbn-invalid-icon"
                src={`${process.env.PUBLIC_URL}/images/error-new.svg`}
                alt="Invalid"
              />
              <span>Enter a valid number of codes between 1 and 5000.</span>
            </div>
          )}

          <input
            id="numberOfCodes"
            type="number"
            className={`input-field ${errors.numberOfCodes ? 'input-error' : ''}`}
            placeholder="Enter number"
            value={numberOfCodes}
            onChange={handleNumberChange}
            min={1}
            max={5000}
            onWheel={() => document.activeElement.blur()}
          />
        </div>

        {/* Reason (optional) */}
        <div className="field-group reason-group">
          <label htmlFor="reason" className="field-label">
            Reason for code request <span className="optional">(optional)</span>
          </label>

          <textarea
            id="reason"
            className="textarea-field"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for this code request"
          />
        </div>

        {/* Print ISBN (optional) */}
        <div className="field-group print-isbn-group">
          <label htmlFor="printISBN" className="field-label">
            Associated Print ISBN <span className="optional">(optional)</span>
          </label>
          <input
            id="printISBN"
            type="text"
            className="input-field"
            value={printISBN}
            onChange={(e) => setPrintISBN(e.target.value)}
            placeholder="Enter the associated Print ISBN"
          />
        </div>

        <button className="generate-button" onClick={handleGenerate}>
          <span className="generate-label">Generate access codes</span>
          <span className="refresh-icon">
            <img
              src={`${process.env.PUBLIC_URL}/images/refresh-light.svg`}
              alt="Generate"
            />
          </span>
        </button>
      </div>
    </div>
  );
};

export default StepTwoComponent;
